import PropTypes from "prop-types";
import * as React from "react";
import { Button, Dialog } from "../themeV2";
import css from "./MessageModal.module.css";

const MessageDialog = React.memo(function MessageDialog({
  buttonProps = {},
  buttonText = "OK",
  confirmButtonProps = {},
  confirmText = "OK",
  message = "This is an alert message",
  onClose = () => {},
  onConfirm = null,
  open = false,
  title = "Alert",
  type = "info",
}) {
  const openRef = React.useRef(open);
  const [internalOpen, setInternalOpen] = React.useState(open);

  React.useEffect(() => {
    if (open !== openRef.current) {
      openRef.current = open;
      setInternalOpen(open);
    }
  }, [open]);

  return (
    <Dialog.Root
      open={internalOpen}
      onOpenChange={(isOpen) => setInternalOpen(isOpen)}
      /* Callers reset the state that renders this dialog here, so it has to wait
         for the exit animation or the content vanishes mid-flight. */
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <Dialog.Popup size="md">
        <Dialog.Title className={type === "error" ? css.errorTitle : undefined}>
          {title}
        </Dialog.Title>

        {/* A div, not the default <p>: callers pass whole forms and paragraphs
            as the message, which a <p> cannot legally contain. */}
        <Dialog.Description render={<div />}>{message}</Dialog.Description>

        <Dialog.Actions>
          {Boolean(buttonText) && (
            <Button
              variant={onConfirm ? "text" : "filled"}
              {...buttonProps}
              onClick={() => setInternalOpen(false)}
            >
              {buttonText}
            </Button>
          )}

          {onConfirm && (
            <Button
              variant="filled"
              color={type === "error" ? "error" : "primary"}
              {...confirmButtonProps}
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          )}
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

MessageDialog.propTypes = {
  buttonProps: PropTypes.object,
  buttonText: PropTypes.string,
  confirmButtonProps: PropTypes.object,
  confirmText: PropTypes.string,
  message: PropTypes.node,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.node,
  type: PropTypes.oneOf(["confirm", "error", "info"]),
};

export default MessageDialog;
